import { DateTime } from 'luxon';
import type {
  ArrangementDto,
  CommandEntryInputDto,
  InstructionInputDto,
  RecurrenceEndInputDto,
  RequirementLineInputDto,
  ScheduleInputDto,
  SharedColumnInputDto,
  SupportingArrangementInputDto,
  Weekday,
} from '@pams/shared';
import { IST_ZONE, cardReportingTimeOf } from '@pams/shared';
import type { PlaceValue } from '@/components/intake/PlaceAutocomplete';
import { submittableCommandEntries } from '@/components/intake/commandAsks';
import { submittableLines } from '@/lib/forceGrid';
import { istNow } from '@/lib/istNow';
import { instructionIsReal, supportingPersonIsReal } from './autosave';

/**
 * ⚠️ RE-EXPORTED, NOT REDEFINED. The watched draft and the shift row moved to
 * `components/intake/draft` when the round-the-clock form started sharing the controls that
 * read them. Re-exported here so every existing import keeps working.
 */
import {
  atLeastOnePlace,
  DEFAULT_NIGHT_SHIFT,
  DEFAULT_SHIFT,
  mintDutyPointId,
  mintShiftId,
  toDraft,
  wholeDaySlotKey,
  type ArrangementDraft,
  type ArrangementPaDraft,
  type RecurrenceEndKind,
  type RecurrenceKind,
  type ShiftRow,
  type TimingKind,
} from '@/components/intake/draft';

export {
  DEFAULT_NIGHT_SHIFT,
  DEFAULT_SHIFT,
  mintDutyPointId,
  mintShiftId,
  toDraft,
  wholeDaySlotKey,
  type ArrangementDraft,
  type ArrangementPaDraft,
  type RecurrenceEndKind,
  type RecurrenceKind,
  type ShiftRow,
  type TimingKind,
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE FORM'S OWN SHAPE, AND THE TWO PLACES IT DIFFERS FROM THE WIRE.
 *
 *  Everything the operator types lives in ONE `Form` store. It is deliberately FLAT where
 *  the wire is nested, for one reason: `scheduleInputSchema` is a discriminated union, and
 *  a `Form.Item` name has to address a field that exists in every arm. `['schedule',
 *  'recurrence', 'endDate']` exists in two arms of three, so binding to it means a control
 *  whose value vanishes when the operator switches segment. Flat fields do not have arms.
 *
 *  `toSchedule` is the ONE crossing, and it is a pure function so it can be unit-tested
 *  without rendering.
 *
 *  ⚠️ THE TIMING CONTROL IS THREE-WAY AND THE WIRE IS TWO-WAY, AND THE MAPPING IS REAL.
 *  §6.1 asks for `No fixed time · Single shift · Multiple shifts`; the contract has
 *  `NO_FIXED_TIME | SHIFTS`, where "single" and "multiple" are the same arm with a
 *  different number of rows. `timingKind` is therefore UI STATE, derived on load and
 *  translated on save — never stored, because two sources of truth for "how many shifts
 *  are there" is how a form comes to disagree with its own strip.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [amended D4] ONE CARD'S WORTH OF FORM STATE, AND IT IS STILL FLAT INSIDE.
 *
 * The reason the arrangement form was flat has not changed — `scheduleInputSchema` is a
 * discriminated union and a `Form.Item` name must address a path that exists in every arm,
 * so `['schedule','recurrence','endDate']` would be a control whose value vanishes when the
 * operator switches segment. That argument applies unchanged one level down: the fields are
 * flat WITHIN a card, and the card is the only nesting.
 *
 * ⚠️ `id` IS MINTED CLIENT-SIDE AND REGISTERED AS A HIDDEN FIELD. `deployments` key on it,
 * `onFinish` receives `getFieldsValue(namePathList)` over REGISTERED fields only, and an
 * unregistered id is an id the payload never carries — CLAUDE.md §8's recorded trap, which
 * has bitten this codebase twice.
 */
export type DutyPointFormValue = {
  id: string;
  name?: string;
  places: PlaceValue[];
  reportingPlace?: PlaceValue;
  /** ⚠️ [C1c §1] Canonical `HH:mm`, or `''` — `TimeField` never emits anything else. */
  reportingTime?: string;
  /** [STAND-DOWN] Canonical `HH:mm`, or `''`. Only a whole-day card sends it — see `standDownTimeOf`. */
  standDownTime?: string;
  /** [C4a §4] Free text, or `''`. Blank goes to the wire as `null` — see `toDutyPointInput`. */
  zone?: string;
  /** [C5c §4] Free text of any length, or `''`. Blank goes to the wire as `null`, exactly as the zone does. */
  sector?: string;
  policyId?: string;
  /** Snapshotted when the card's policy is chosen, so the grid resolves what the card NAMES. */
  policyVersionId?: string;
  /**
   * ⚠️ [amended C1a] WHICH VERSION THE COLUMNS ON SCREEN CAME FROM. NOT THE BINDING.
   *
   * `policyVersionId` answers "which version will this be saved against" and is written by
   * the SERVER (absent means "resolve the current one" — [PRE-P6] §3a, and re-pinning it
   * client-side is precisely what that pass deleted). This answers a different question:
   * "which version were these requirement lines seeded from". They diverge exactly when
   * somebody publishes a new version while a draft card is open, which is C1a §2.
   *
   * ⚠️ FORM-ONLY, AND IT NEVER REACHES THE WIRE. `body()` maps every card through
   * `toDutyPointInput` below, which enumerates its fields explicitly — there is no
   * `...point` spread anywhere on that path, so an extra key here cannot leak.
   */
  columnsFromVersionId?: string;
  timingKind: TimingKind;
  shifts: ShiftRow[];
  recurrenceKind: RecurrenceKind;
  startDate?: DateTime;
  endsKind: RecurrenceEndKind;
  endDate?: DateTime;
  occurrences?: number;
  weekdays: Weekday[];
  commandEntries: CommandEntryInputDto[];
  requirementLines: RequirementLineInputDto[];
  /** ⚠️ [D4.3] The point's OWN. `ArrangementFormValues` keeps the arrangement's, separately. */
  instructions: InstructionInputDto[];
  supportingArrangements: SupportingArrangementInputDto[];
};

export type ArrangementFormValues = {
  paDate?: DateTime;
  paNumber?: string;
  title?: string;
  description?: string;
  /** ⚠️ [amended D4] FREE TEXT. "about 20,000" is a real answer; a number is not. */
  estimatedCrowdSize?: string;
  /**
   * ⚠️ [C1b §4] IDS ONLY, AND `fromArrangement` MUST PROJECT IT.
   *
   * rc-field-form re-sends every REGISTERED field on `onFinish`, so a field this projection
   * omits is RESET to `undefined` on the next autosave — [INGEST.2]'s "every form in this
   * repository re-sends untouched values", seen from the other side. The resolved rows the
   * PICKER needs for its labels come from the record, not from here.
   *
   * ⚠️ AND IT IS DELIBERATELY NOT ON `ArrangementDraft`. No derivation reads it — no checklist
   * rule, no preview — and [C1a] records what the watched draft costs when it carries a field
   * a derivation DOES read and the projection forgot. State the rule here for the day one does.
   */
  associatedOrgUnitIds: string[];
  dutyPoints: DutyPointFormValue[];
  /**
   * ⚠️ [C5c §2] WHICH COLUMNS ARE ASKED FOR ONCE ACROSS EVERY CARD, AND EACH COVERED CARD'S OWN
   * NUMBER, SET ASIDE. Registered by a hidden `Form.Item` beside `dutyPoints` ([P3a.1]'s trap: an
   * unregistered path never reaches `onFinish`) and PROJECTED by `fromArrangement` below, or the
   * first autosave after a reload sends `[]` and ends every share with its stash discarded — the
   * failure the required key on `arrangementSchema.sharedColumns` exists to make impossible.
   * `lib/sharedColumns.ts` is the only thing that writes it.
   */
  sharedColumns: SharedColumnInputDto[];
  instructions: InstructionInputDto[];
  supportingArrangements: SupportingArrangementInputDto[];
};

/** Retained ONLY as the per-card shape's ancestor — see `DutyPointFormValue` above. */
type LegacyFlatFields = {
  timingKind: TimingKind;
  shifts: ShiftRow[];
  recurrenceKind: RecurrenceKind;
  startDate?: DateTime;
  /**
   * ⚠️ WHEN THE RECURRENCE STOPS. `ON_DATE` reads `endDate`; `AFTER_N` reads `occurrences`.
   * Two flat fields on the form and a nested union on the wire, because the FORM is flat by
   * construction — rc-field-form addresses a path, not a variant — and the WIRE has to make
   * the illegal both-at-once state unrepresentable. `toSchedule` is the one crossing.
   */
  endsKind: RecurrenceEndKind;
  endDate?: DateTime;
  occurrences?: number;
  weekdays: Weekday[];
};

/** The form's flat fields → the wire's discriminated union. The one crossing, per card. */
export const toSchedule = (values: LegacyFlatFields & { paDate?: DateTime }): ScheduleInputDto | undefined => {
  const iso = (value: DateTime | undefined): string | undefined => value?.toISODate() ?? undefined;
  const anchor = iso(values.paDate);
  const start = iso(values.startDate) ?? anchor;
  const end = iso(values.endDate) ?? start;
  if (!start || !end) return undefined;

  const ends: RecurrenceEndInputDto =
    values.endsKind === 'AFTER_N' && values.occurrences
      ? { kind: 'AFTER_N', occurrences: values.occurrences }
      : { kind: 'ON_DATE', endDate: end };

  const recurrence =
    values.recurrenceKind === 'ONE_DAY'
      ? ({ kind: 'ONE_DAY', date: start } as const)
      : values.recurrenceKind === 'DATE_RANGE'
        ? ({ kind: 'DATE_RANGE', startDate: start, ends } as const)
        : ({ kind: 'WEEKDAYS', startDate: start, ends, weekdays: values.weekdays } as const);

  const timing =
    values.timingKind === 'NO_FIXED_TIME'
      ? ({ kind: 'NO_FIXED_TIME' } as const)
      : ({
          kind: 'SHIFTS',
          /*
           * "Single" is the same arm with one row.
           *
           * ⚠️ THE BELT TO THE HANDLER'S BRACES, NOT THE RULE ITSELF. `onTimingChange` now
           * truncates the rows on the segment click, so by the time anything reaches here
           * there is one. This stays because a stored draft written before that fix can
           * still hydrate with two, and because the wire shape must not depend on a click
           * having happened. It used to be the ONLY truncation, which is how the screen and
           * the record came to disagree about how many shifts there were.
           */
          shifts: values.timingKind === 'SINGLE' ? values.shifts.slice(0, 1) : values.shifts,
        } as const);

  return { recurrence, timing };
};

/**
 * And back, for editing an existing arrangement.
 *
 * ⚠️ IT TAKES THE DTO AND NOTHING ELSE. It used to ask the caller for `paDate` and a
 * date-parsing function, and that is exactly why it shipped exported and never called:
 * wiring it up needed three arguments the page did not have to hand, so the effect that
 * should have called it seeded the title instead and the rest of a saved arrangement was
 * silently dropped on every edit. A function that is awkward to call is a function that
 * does not get called.
 */
/** Business dates arrive as `YYYY-MM-DD`; the pickers hold luxon. One conversion, here. */
const asDate = (iso: string): DateTime => DateTime.fromISO(iso, { zone: IST_ZONE });

/** One stored duty point → one card's worth of form state. [amended D4] */
const toDutyPointValue = (point: ArrangementDto['dutyPoints'][number]): DutyPointFormValue => {
  const { recurrence, timing } = point.schedule;
  const shifts = timing.kind === 'SHIFTS' ? timing.shifts : [];

  return {
    id: point.id,
    name: point.name ?? undefined,
    /* ⚠️ [D4.5 §2b] Floored — a point saved with no place must reopen with an INPUT. */
    places: atLeastOnePlace(point.places),
    reportingPlace: point.reportingPlace
      ? { placeId: point.reportingPlace.placeId, name: point.reportingPlace.name }
      : undefined,
    reportingTime: point.reportingTime ?? undefined,
    standDownTime: point.standDownTime ?? undefined,
    zone: point.zone ?? undefined,
    sector: point.sector ?? undefined,
    policyId: point.policyId,
    policyVersionId: point.policyVersionId,
    // A stored card is pinned, so its columns and its binding are the same version.
    columnsFromVersionId: point.policyVersionId,
    // Derived, never stored — see the header.
    timingKind: timing.kind === 'NO_FIXED_TIME' ? 'NO_FIXED_TIME' : shifts[1] ? 'MULTIPLE' : 'SINGLE',
    shifts: shifts.map((shift) => ({
      id: shift.id ?? mintShiftId(),
      name: shift.name ?? null,
      startTime: shift.startTime,
      endTime: shift.endTime,
    })),
    recurrenceKind: recurrence.kind,
    startDate: asDate(recurrence.kind === 'ONE_DAY' ? recurrence.date : recurrence.startDate),
    endsKind: recurrence.kind === 'ONE_DAY' ? 'ON_DATE' : recurrence.ends.kind,
    /*
     * ⚠️ UNDEFINED FOR A COUNT, NEVER THE RESOLVED DATE. Seeding the picker with the date
     * the count worked out to would show both controls populated; the next save would read
     * the date, flip the mode, and "ten times" would silently become "until 16 Sep" — after
     * which moving the start date changes the NUMBER of duties instead of moving the same
     * ten.
     */
    endDate:
      recurrence.kind === 'ONE_DAY' || recurrence.ends.kind === 'AFTER_N' ? undefined : asDate(recurrence.ends.endDate),
    occurrences:
      recurrence.kind !== 'ONE_DAY' && recurrence.ends.kind === 'AFTER_N' ? recurrence.ends.occurrences : undefined,
    weekdays: recurrence.kind === 'WEEKDAYS' ? [...recurrence.weekdays] : [],
    commandEntries: point.commandEntries.map((entry) => ({
      roleKey: entry.roleKey,
      adHocLabel: entry.adHocLabel,
      officerIds: [...entry.officerIds],
      perSlot: entry.perSlot.map((row) => ({ ...row, officerIds: [...row.officerIds] })),
      /*
       * ⚠️ [C6a] CARRIED — [D4.3]'s rule, and on an ISSUED order its absence is a 409 on every save.
       * `asksOnIssuedOrder` compares the incoming asks with the stored ones, so a reload that dropped
       * them would make the next autosave read as "this role is no longer asked of Central", which the
       * server refuses. The key is added only where the DTO has it, so a by-name card round-trips
       * byte for byte. The seat a division has answered with is NOT carried: it is read-only, and
       * `commandAskSeatSchema`'s header says why it must never ride back as `officerIds`.
       */
      ...(entry.asks === undefined
        ? {}
        : {
            asks: entry.asks.map((ask) => ({
              askId: ask.askId,
              divisionOrgUnitId: ask.division.id,
              unitOrgUnitId: ask.unit?.id ?? null,
            })),
          }),
    })),
    requirementLines: point.requirementLines.map((line) => ({
      postTypeId: line.postTypeId,
      vehicleTypeId: line.vehicleTypeId,
      adHocLabel: line.adHocLabel,
      /*
       * ⚠️ [C5c §2] CARRIED, AND ITS ABSENCE WAS A DEFECT. A one-off column's post restriction
       * ([C1c §4]) was dropped here, so the first autosave after a reload sent the line without it
       * and the server read that as "any post" — the restriction vanished with nothing on screen to
       * say so. `?? []` for a cached pre-[C1c] response, which lacks the key.
       */
      restrictToPostTypeIds: [...(line.restrictToPostTypeIds ?? [])],
      requiredCount: line.requiredCount,
      allocations: line.allocations.map((allocation) => ({
        divisionOrgUnitId: allocation.divisionOrgUnitId,
        unitOrgUnitId: allocation.unitOrgUnitId,
        count: allocation.count,
      })),
    })),
    /*
     * [D4.3] The point's OWN standing orders. The arrangement's general ones stay at the root.
     *
     * ⚠️ `?? []` ON A FIELD THE DTO TYPES AS REQUIRED, AND IT IS NOT BELT-AND-BRACES.
     * This is a WIRE crossing: the type describes what a CURRENT server sends, and a browser
     * holding a cached response from a build made before this pass — or a fixture written
     * against the old shape — sends an object without the key. `point.instructions.map` on
     * that throws inside a mount effect, which React surfaces as a blank page rather than as
     * a missing panel. Every other optional-on-the-wire field here is read the same way.
     */
    instructions: (point.instructions ?? []).map((row) => ({ type: row.type, text: row.text })),
    supportingArrangements: (point.supportingArrangements ?? []).map((row) => ({
      personName: row.personName,
      responsibilities: [...row.responsibilities],
    })),
  };
};

/**
 * ⚠️ [C2b §8g] TODAY, OR THE ORDER DATE IF IT IS LATER. Compared on the CALENDAR DAY rather
 * than the instant: two `DateTime`s on the same day differ by hours, and `>` on the instants
 * would re-anchor an order dated today onto "today at the current time", which is the same day
 * with a different value and would mark the form dirty on open.
 *
 * ⚠️ [amended P7C §6a] "TODAY" IS THE IST DAY, AND THE COMPARISON IS OF `toISODate()` STRINGS.
 * It read `DateTime.now()`, the BROWSER's day — on a laptop set to UTC that is yesterday from
 * 18:30 to 24:00 UTC, so a new card was seeded on a date publish's past-date blocker (which reads
 * `istDutyDate(now)`) then refused. And comparing `startOf('day')` INSTANTS across two zones asks
 * which midnight came first, not which calendar day is later: a picked date (browser zone) and
 * an IST "today" on the same calendar day are hours apart. A `YYYY-MM-DD` string compares as the
 * calendar day in either zone, which is the question. Moved here from the page so it can be
 * tested at a fixed instant in a foreign zone (`formValues.test.ts`).
 */
export const laterOfTodayAnd = (paDate: DateTime | undefined): DateTime => {
  const today = istNow();
  if (!paDate?.isValid) return today;
  return (paDate.toISODate() ?? '') > (today.toISODate() ?? '') ? paDate : today;
};

/**
 * ⚠️ [amended D4] A NEW ORDER OPENS WITH NO DUTY POINT, AND THAT IS §5b's EMPTY STATE.
 *
 * Tab 1 asks for an order date and nothing else, so the first autosave carries exactly
 * that. Seeding a blank card here would put an unpublishable card on every new order and
 * make "add the first one" a lie.
 *
 * ⚠️ [amended P7C §6a] THE ORDER DATE IS TODAY IN IST, NEVER THE BROWSER'S TODAY — the same
 * defect as `laterOfTodayAnd` above, one field earlier.
 */
export const emptyValues = (): ArrangementFormValues => ({
  paDate: istNow(),
  associatedOrgUnitIds: [],
  dutyPoints: [],
  sharedColumns: [],
  instructions: [],
  supportingArrangements: [],
});

export const fromArrangement = (arrangement: ArrangementDto): ArrangementFormValues => ({
  paDate: asDate(arrangement.paDate),
  paNumber: arrangement.paNumber ?? undefined,
  title: arrangement.title ?? undefined,
  description: arrangement.description ?? undefined,
  estimatedCrowdSize: arrangement.estimatedCrowdSize ?? undefined,
  associatedOrgUnitIds: arrangement.associatedOrgUnits.map((row) => row.unit.id),
  dutyPoints: arrangement.dutyPoints.map(toDutyPointValue),
  /*
   * ⚠️ [C5c §2] THE INPUT SHAPE — the reader's `label`/`shortLabel`/`kind` are the server's to say
   * and never go back. `?? []` for a cached response from before the key existed.
   */
  sharedColumns: (arrangement.sharedColumns ?? []).map((entry) => ({
    lineKey: entry.lineKey,
    anchorDutyPointId: entry.anchorDutyPointId,
    stashed: entry.stashed.map((row) => ({
      dutyPointId: row.dutyPointId,
      requiredCount: row.requiredCount,
      displayOrder: row.displayOrder,
      restrictToPostTypeIds: [...row.restrictToPostTypeIds],
      allocations: row.allocations.map((allocation) => ({ ...allocation })),
    })),
  })),
  instructions: arrangement.instructions.map((row) => ({ type: row.type, text: row.text })),
  supportingArrangements: arrangement.supportingArrangements.map((row) => ({
    personName: row.personName,
    responsibilities: [...row.responsibilities],
  })),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C5b §3e] A CARD'S REPORTING TIME ON THE WIRE — `cardReportingTimeOf`, AND NOTHING ELSE.
 *
 *  A whole-day card sends the time the operator picked; a shift-bearing card sends `null`, because
 *  each of its duties reports at its OWN shift start — the server stamps and derives that per duty
 *  (`reportingInstantOf`). Decision 4, which REVERSES [C2b §1c] for shift cards.
 *
 *  ⚠️ WHAT [C2b §1c] DID, AND WHY IT WENT. It derived a shift card's reporting time as the
 *  lexicographic MINIMUM of its shift starts, floored at `DEFAULT_SHIFT()`'s start, and publish
 *  stamped that one instant on every duty of the card — so a card with a 05:30 and a 17:00 shift
 *  told the evening duty to report at 05:30. Nine such duties are live on `pamsDev`. It also
 *  read rows a SINGLE card will not save (`toSchedule` keeps only the first), which was a second
 *  route to a wrong time.
 *
 *  ⚠️ ONE RULE, SHARED WITH THE SERVER. This used to be the only statement of it; now the server's
 *  stored-card writer and every reader of a card call the same function from `@pams/shared`, so
 *  the builder and the record cannot disagree about whether a card has a time.
 *
 *  ⚠️ STILL DERIVED AT THE WIRE CROSSING RATHER THAN BY AN EFFECT, for [C2b §1c]'s own reason: an
 *  effect writing back into the form on every edit is a cascading render `eslint-plugin-react-hooks`
 *  refuses, and it would fight the operator mid-keystroke.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const reportingTimeOf = (point: DutyPointFormValue): string | null =>
  cardReportingTimeOf({ timingKind: point.timingKind, reportingTime: point.reportingTime });

/**
 * [STAND-DOWN] A card's stand-down time on the wire: what a whole-day card holds, `null` otherwise.
 *
 * ⚠️ A SHIFT-BEARING CARD SENDS `null` WHATEVER THE FORM STILL HOLDS. A shift already states its
 * end, and switching a card from whole-day to shifts leaves the old value in the store — sending it
 * would put a second, contradictory end time on the record. The service nulls it too.
 */
export const standDownTimeOf = (point: DutyPointFormValue): string | null =>
  point.timingKind === 'NO_FIXED_TIME' && (point.standDownTime ?? '').trim().length > 0
    ? (point.standDownTime ?? null)
    : null;

/**
 * A card's form state → the wire's duty point. The submit-side crossing. [amended D4]
 *
 * ⚠️ `submittableLines` STRIPS ZERO-COUNT SOURCES, exactly as it did before — a source row
 * being typed into must survive on screen and must not reach the wire empty.
 */
export const toDutyPointInput = (point: DutyPointFormValue, paDate: DateTime | undefined) => ({
  id: point.id,
  name: point.name ?? null,
  places: point.places.filter((place) => (place?.name ?? '').trim().length > 0),
  reportingPlace: (point.reportingPlace?.name ?? '').trim().length > 0 ? point.reportingPlace : null,
  /* ⚠️ [amended C5b §3e] Picked on a whole-day card; `null` on a shift card, whose duties report at their own start. */
  reportingTime: reportingTimeOf(point),
  standDownTime: standDownTimeOf(point),
  /* ⚠️ [C4a §4] Trimmed, and blank is `null` — a card with no zone must carry none, not `''`. */
  zone: (point.zone ?? '').trim().length > 0 ? (point.zone ?? '').trim() : null,
  /* ⚠️ [C5c §4] The zone's rule, and WHOLE — trimmed, never sliced: the client asked for no length limit. */
  sector: (point.sector ?? '').trim().length > 0 ? (point.sector ?? '').trim() : null,
  policyId: point.policyId,
  policyVersionId: point.policyVersionId,
  schedule: toSchedule({ ...point, paDate }),
  requirementLines: submittableLines(point.requirementLines),
  /* ⚠️ [C6a] A half-built ask (no division yet) is dropped through ONE predicate — `commandAskIsReal`. */
  commandEntries: submittableCommandEntries(point.commandEntries),
  /*
   * ⚠️ [D4.3] A BLANK PERSON ROW IS DROPPED ON THE WAY OUT, exactly as a blank PLACE is.
   * `Add person` seeds an empty name so the operator has something to type into, and
   * `supportingArrangementInputSchema` requires one — so without this the whole autosave 422s
   * on a row the operator has not finished, which is divergence-register #30's shape.
   * ⚠️ [C5c §8e] Through `autosave.ts`' predicates now — the one spelling of "a row the wire keeps".
   */
  instructions: (point.instructions ?? []).filter(instructionIsReal),
  supportingArrangements: (point.supportingArrangements ?? []).filter(supportingPersonIsReal),
});

/**
 * The same translation as `toSchedule`, from the WATCHED draft rather than the form store.
 *
 * The page needs it this way round: a `useMemo` over `form.getFieldsValue()` is not
 * reactive, so it would either never recompute or recompute on every render. The draft is
 * a watched value, so it changes exactly when the schedule does.
 */
export const scheduleOfDraft = (draft: ArrangementDraft): ScheduleInputDto | undefined => {
  const start = draft.startDate || draft.paDate;
  const end = draft.endDate || start;
  if (!start) return undefined;

  const ends: RecurrenceEndInputDto =
    draft.endsKind === 'AFTER_N' && draft.occurrences
      ? { kind: 'AFTER_N', occurrences: draft.occurrences }
      : { kind: 'ON_DATE', endDate: end };

  const recurrence =
    draft.recurrenceKind === 'ONE_DAY'
      ? ({ kind: 'ONE_DAY', date: start } as const)
      : draft.recurrenceKind === 'DATE_RANGE'
        ? ({ kind: 'DATE_RANGE', startDate: start, ends } as const)
        : ({ kind: 'WEEKDAYS', startDate: start, ends, weekdays: draft.weekdays as Weekday[] } as const);

  const timing =
    draft.timingKind === 'NO_FIXED_TIME'
      ? ({ kind: 'NO_FIXED_TIME' } as const)
      : ({ kind: 'SHIFTS', shifts: draft.timingKind === 'SINGLE' ? draft.shifts.slice(0, 1) : draft.shifts } as const);

  return { recurrence, timing };
};
